import * as React from 'react';

import { VariantProps, cva } from 'class-variance-authority';

import { cn } from '../utils';

export type BadgeColor =
    | 'gray'
    | 'aqua'
    | 'buttercup'
    | 'sienna'
    | 'cerulean'
    | 'purple'
    | 'bluewood';

export type BadgeVariant = 'solid' | 'subtle' | 'outline';

const badgeVariants = cva(
    'inline-flex items-center rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 uppercase tracking-[0.0175em]',
    {
        variants: {
            variant: {
                solid: 'border-transparent',
                subtle: 'bg-white border-transparent',
                outline: 'bg-transparent',
            },
            color: {
                gray: '',
                aqua: '',
                buttercup: '',
                sienna: '',
                cerulean: '',
                purple: '',
                bluewood: '',
            },
            size: {
                sm: 'px-1.5 py-0.5 font-bold text-sm leading-4 h-5',
                lg: 'px-1.5 py-0.5 font-bold text-lg leading-5 h-7',
            },
        },
        compoundVariants: [
            // Solid variants
            {
                variant: 'solid',
                color: 'gray',
                class: 'bg-gray-500 text-white ',
            },
            {
                variant: 'solid',
                color: 'aqua',
                class: 'bg-aqua-500 text-white ',
            },
            {
                variant: 'solid',
                color: 'buttercup',
                class: 'bg-buttercup-500 text-white ',
            },
            {
                variant: 'solid',
                color: 'sienna',
                class: 'bg-sienna-500 text-white ',
            },
            {
                variant: 'solid',
                color: 'cerulean',
                class: 'bg-cerulean-500 text-white ',
            },
            {
                variant: 'solid',
                color: 'purple',
                class: 'bg-purple-500 text-white',
            },
            {
                variant: 'solid',
                color: 'bluewood',
                class: 'bg-bluewood-500 text-white ',
            },
            // Subtle variants
            {
                variant: 'subtle',
                color: 'gray',
                class: 'bg-gray-50 text-gray-800 ',
            },
            {
                variant: 'subtle',
                color: 'aqua',
                class: 'bg-aqua-50 text-aqua-800 ',
            },
            {
                variant: 'subtle',
                color: 'buttercup',
                class: 'bg-buttercup-50 text-buttercup-800 ',
            },
            {
                variant: 'subtle',
                color: 'sienna',
                class: 'bg-sienna-50 text-sienna-800 ',
            },
            {
                variant: 'subtle',
                color: 'cerulean',
                class: 'bg-cerulean-50 text-cerulean-800 ',
            },
            {
                variant: 'subtle',
                color: 'purple',
                class: 'bg-purple-50 text-purple-800 ',
            },
            {
                variant: 'subtle',
                color: 'bluewood',
                class: 'bg-bluewood-50 text-bluewood-800 ',
            },
            // Outline variants
            {
                variant: 'outline',
                color: 'gray',
                class: 'border border-solid border-gray-300 text-gray-800',
            },
            {
                variant: 'outline',
                color: 'aqua',
                class: 'border border-solid border-aqua-300 text-aqua-800',
            },
            {
                variant: 'outline',
                color: 'buttercup',
                class: 'border border-solid border-buttercup-300 text-buttercup-800',
            },
            {
                variant: 'outline',
                color: 'sienna',
                class: 'border border-solid border-sienna-300 text-sienna-800',
            },
            {
                variant: 'outline',
                color: 'cerulean',
                class: 'border border-solid border-cerulean-300 text-cerulean-800',
            },
            {
                variant: 'outline',
                color: 'purple',
                class: 'border border-solid border-purple-300 text-purple-800',
            },
            {
                variant: 'outline',
                color: 'bluewood',
                class: 'border border-solid border-bluewood-300 text-bluewood-800',
            },
        ],
        defaultVariants: {
            variant: 'subtle',
            color: 'cerulean',
            size: 'sm',
        },
    },
);

export interface BadgeProps
    extends React.HTMLAttributes<HTMLDivElement>,
        Omit<VariantProps<typeof badgeVariants>, 'variant'> {
    color: BadgeColor;
    label: string | React.ReactNode;
    size?: 'sm' | 'lg';
    icon?: React.ReactNode;
    variant?: BadgeVariant | 'flat' | 'outlined'; // Allow 'flat' and 'outlined' for backward compatibility
}

function Badge({ className, variant, color, label, size, icon, ...props }: BadgeProps) {
    // Map legacy variants for backward compatibility
    let mappedVariant: BadgeVariant;
    switch (variant) {
        case 'flat':
            mappedVariant = 'subtle';
            break;
        case 'outlined':
            mappedVariant = 'outline';
            break;
        case undefined:
        case null:
            mappedVariant = 'subtle';
            break;
        default:
            mappedVariant = variant as BadgeVariant;
    }

    let badgeSize: 'sm' | 'lg' | undefined = size;
    if (!size) {
        badgeSize = 'sm';
    }

    return (
        <div
            className={cn(
                badgeVariants({ variant: mappedVariant, color, size: badgeSize }),
                className,
            )}
            {...props}
        >
            {icon && (
                <span
                    className={cn(
                        'flex-shrink-0 flex items-center',
                        badgeSize === 'sm' && 'w-3 h-3 mr-0.5',
                        badgeSize === 'lg' && 'w-4 h-4 mr-1',
                        !badgeSize && 'w-3 h-3 mr-0.5',
                    )}
                >
                    {icon}
                </span>
            )}
            <span className="truncate min-w-0">{label}</span>
        </div>
    );
}

export { Badge, badgeVariants };
